import { withTheme, keyframes, css } from '@emotion/react';
import { withRouter } from '../../helpers/withRouter';
import React, { useState, useEffect, useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import Select, { components, createFilter } from 'react-select';
import AsyncInput from './AsyncInput';
import styled from '@emotion/styled';
import EnergyTop1 from '../../assets/images/design_elements/energy-arc-top.svg';
import EnergyBottom1 from '../../assets/images/design_elements/energy-arc-bottom.svg';
import SearchMag from '../../assets/images/icons/search_mag.svg';
import mobileDetector from '../../helpers/mobileDetector';
import EnvContext from '../../EnvContext';

const { ValueContainer, Placeholder } = components;

const topIn = keyframes`
  0%, 50% {
    transform: rotate(-180deg) translateX(-100%);
  }
  65% {
    opacity: 0;
  }
  100% {
    opacity: 1;
    transform: rotate(0deg) translateX(5%);
  }
`;

const bottomIn = keyframes`
  0%, 50% {
    transform: rotate(-180deg) translateX(100%);
  }
  65% {
    opacity: 0;
  }
  100% {
    opacity: 1;
    transform: rotate(0deg) translateX(5%);
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  font-family: ${props => props.theme.fonts.primary};
  justify-content: space-between;
  max-width: 1000px;
  width: 100%;

  ${props => props.hideMenu && css`
    display: none;
  `};

  ${props => props.simplified && css`
    position: fixed;
    z-index: 1;
    padding: 20px;
    background-color: ${props.theme.colors.contrast2};
    top: 70px;
  `}

  ${props => props.simplified && props.isMobile && css`
    border-color: ${props.theme.colors.neutral2};
    border-width: 0 0 1px;
    border-style: solid;
    padding-bottom: 54px;
  `}

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: ${props => props.simplified ? "row" : "column"};

    ${props => props.simplified && css`

    `}
  }
`;

const InputWrapper = styled.div`
  color: ${props => props.theme.colors.primary1};
  position: relative;
  width: 100%;

  ${props => (props.countryCodePresent && !props.simplified) && css`
    margin-left: 8px;
    margin-bottom: 16px;
    flex: 1 0 50%;
  `}

  ${props => (props.countryCodePresent && props.simplified) && css`
    margin-left: 12px;
  `}

  ${props => (!props.countryCodePresent && props.simplified) && css`
    margin-left: 20px;
  `}

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    ${props => props.simplified && css`
      width: calc(100% - 22px);
    `}

    ${props => props.countryCodePresent && css`
      margin-right: 12px;
    `}

    ${props => (props.countryCodePresent && !props.simplified) && css`
      margin-bottom: 0px;
      flex: unset;
    `}

    &:after {
      top: 7px;
      left: ${props => props.simplified ? '24px' : '12px'};
    }
  }
`;

const FakePlaceholder = styled.label`
  &:after {
    content: "Postal Code";
    color: ${props => props.theme.colors.neutral3};
    display: ${props => props.zipFocus || (props.hasValue && !props.zipFocus) ? 'none' : 'block'};
    position: absolute;
    top: ${props => props.simplified ? '29px' : '20px'};
    left: ${props => props.simplified ? '12px' : '12px'};
    pointer-events: none;
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    &:after {
      top: ${props => props.simplified ? '18px' : '20px'};
      left: ${props => props.simplified ? '24px' : '12px'};
    }
  }
`;

const StyledInput = styled.input`
  background-color: ${props => props.theme.colors.neutral2};
  border-color: ${props => props.zipError ? props.theme.colors.error : props.hasValue && props.zipFocus ? props.theme.colors.primary1 : props.theme.colors.primary4};
  border-radius: 6px 6px 0 0;
  border-style: solid;
  border-width: 0px 0px 3px;
  color: ${props => props.hasValue ? props.theme.colors.neutral1 : props.theme.colors.neutral3};
  font-size: ${props => props.theme.fontSizes.sm};
  height: 54px;
  outline: none;
  padding-left: 12px;
  width: 100%;

  &:hover {
    border-color: ${props => props.theme.colors.primary1};
  }

  ${props => props.hasValue && css`
    padding-top: 12px;
  `}

  ${props => props.simplified && css`
    margin: 12px 0;
    width: 100%;
  `}

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    ${props => props.simplified && css`
      margin: 0 12px;
      width: 100%;
    `}

    ${props => (props.simplified && props.countryCodePresent) && css`
      margin: 0 12px;
      width: calc(100% - 12px);
    `}
  }
`;

const StyledButton = styled.input`
  background: ${props => props.theme.colors.primary1};
  border: none;
  border-radius: 6px;
  color: ${props => props.theme.colors.contrast2};
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: bold;
  outline: none;
  padding: 12px;
  margin-top: 12px;
  transition: all .25s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.secondary3};
  }
  &:focus {
    background-color: ${props => props.theme.colors.secondary4};
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    align-self: center;
    width: 346px;
    margin-top: ${props => props.theme.margins.std};
    ${props => props.simplified && css`
      margin-left: 12px;
      margin-top: 0px;
    `}
  }
`;

const FormWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: ${props => props.simplified ? '0px' : '80px 0 80px'};
  position: relative;

  ${props => props.simplified && css`
    width: 100%;
  `}

  ${props => props.removeTopPadding && css`
    padding-top: 0;
  `}

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    padding: ${props => props.simplified ? '0px' : '110px 0 90px'};
    ${props => props.removeTopPadding && css`
      padding-top: 0;
    `}
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: ${props => props.simplified ? '0px' : '130px 0 110px'};
    ${props => props.removeTopPadding && css`
      padding-top: 0;
    `}
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: ${props => props.simplified ? '0px' : '130px 0 110px'};
    ${props => props.removeTopPadding && css`
      padding-top: 0;
    `}
  }
`;

const Title = styled.div`
  ${props => props.simplified && css`
    display: none;
  `}
  color: ${props => props.theme.colors.primary4};
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.lg};
  margin-bottom: ${props => props.theme.margins.xxxs};
  padding: ${props => props.paddingValue ? `0 ${props.paddingValue} 0 0` : '0px'};
  text-align: center;
  font-weight: bold;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {

  }
`;

const InnerTitle = styled.div`
  ${props => props.simplified && css`
    display: none;
  `}
  color: ${props => props.theme.colors.primary4};
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.lg};
  margin-bottom: 0;
  padding: ${props => props.paddingValue ? `0 ${props.paddingValue} 0 0` : '0px'};
  text-align: center;
  font-weight: bold;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    margin-bottom: 0;
  }
`;

const InputContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  margin-top: ${props => props.simplified ? '0px' : props.theme.margins.xxxs};
  ${props => props.simplified && css`
    width: 100%;
  `}

  ${props => (props.countryCodePresent) && css`
    flex-wrap: wrap;
  `}

  ${props => (!props.countryCodePresent) && css`
    margin-right: 12px;
  `}

  ${props => (!props.countryCodePresent && props.simplified) && css`
    margin-right: 20px;
  `}

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    flex-wrap: unset;

    ${props => (props.simplified && props.countryCodePresent) && css`
      width: calc(200% - 12px);
      margin: 0 12px 0 0;
    `}
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    flex-wrap: unset;

    ${props => (props.simplified && props.countryCodePresent) && css`
      width: 200%;
      margin: 0;
    `}
  }
`;

const SelectToggle = styled.span`
  display: ${props => props.simplified ? 'inline-block' : 'none'};
  ${props => props.simplified && css`
    width: 100%;
  `}

  ${props => (props.simplified && props.countryCodePresent && !props.isMobile) && css`
    margin-left: 12px;
  `}

  ${props => props.countryCodePresent && css`
    margin-top: 16px;
  `}

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    margin-top: 0;
  }
`;

const SubHeader = styled.div`
  ${props => props.simplified && css`
    display: none;
  `}

  ${props => props.hide && css`
    opacity: 0;
  `}
  color: ${props => props.theme.colors.primary4};
  font-family: ${props => props.theme.fonts.secondary};
  font-size: ${props => props.theme.fontSizes.sm};
  line-height: ${props => props.theme.lineHeights.std};
  margin-top: ${props => props.simplified ? '0px' : props.theme.margins.sm};
  text-align: center;

  span {
    font-weight: bold;
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    margin-top: ${props => props.theme.margins.xs};
  }
`;

const AnimationWrapper = styled.div`
  ${props => (props.simplified || props.hide ) && css`
    display: none;
  `}
  height: 70px;
  overflow: hidden;
  position: absolute;
  top: 0px;
  width: 90%;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    height: 110px;
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    height: 120px;
  }
`;

const AnimationWrapperBottom = styled.div`
  ${props => props.simplified && css`
    display: none;
  `}
  height: 70px;
  overflow: hidden;
  position: absolute;
  bottom: 0px;
  width: 90%;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    height: 90px;
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    height: 120px;
  }
`;

const AnimationTop = styled.div`
  ${props => props.inView && css`
    animation-name: ${topIn};
    animation-duration: 1s;
    animation-fill-mode: forwards;
  `}
  animation-timing-function: ease;
  background-image: url(${EnergyTop1});
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  height: 70px;
  opacity: 0;
  position: absolute;
  top: 0px;
  transform: rotate(-180deg) translateX(-100%);
  transform-origin: center bottom;
  width: 90%;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    height: 90px;
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    height: 120px;
  }
`;

const AnimationBottom = styled.div`
  ${props => props.inView && css`
    animation-name: ${bottomIn};
    animation-duration: 1s;
    animation-fill-mode: forwards;
  `}
  animation-timing-function: ease;
  background-image: url(${EnergyBottom1});
  background-repeat: no-repeat;
  background-size: contain;
  bottom: 0px;
  height: 70px;
  position: absolute;
  transform: rotate(180deg) translateX(100%);
  transform-origin: center top;
  width: 90%;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    height: 90px;
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    height: 120px;
  }
`;

const ErrorMessage = styled.div`
  display: ${props => props.zipError ? 'block' : 'none' };
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  padding-top: 6px;
  position: absolute;
  min-width: 400px;
  ${props => props.simplified && css`
    left: 12px;
  `}
`;

const ClearInput = styled.div`
  padding: 0px 5px; 
  cursor: pointer;
  border-radius: 20px;
  border: 2px solid;
  padding: 2px 5px 1px;
  position: absolute;
  font-weight: bold;
  font-size: 12px;
  color: #828282;
  border-color: #828282;
  font-family: "Proxima Nova", Helvetica, sans-serif;
  top: ${props => props.simplified ? "27px" : "16px"};
  right: ${props => props.simplified ? '-3px' : '8px'};
  line-height: 16px;
  display: ${props => props.hasValue ? 'flex' : 'none'};
`;

const MobileMenu = styled.div`
  width: 100%;
  height: 104px;
  background-color: ${props => props.theme.colors.neutral2};
  position: fixed;
  top: 54px;
  padding: 12px 16px;
  z-index: 1;

  ${props => props.hideMenu && css`
    display: none;
  `}
`;

const MobileHeader = styled.div`
  background-image: url(${SearchMag});
  background-repeat: no-repeat;
  background-position: left center;
  padding-left: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  font-family: ${props => props.theme.fonts.primary};
  font-weight: bold;
  color: ${props => props.theme.colors.primary4};
`;

const ExpandButton = styled.div`
  color: ${props => props.theme.colors.primary1};
  font-family: ${props => props.theme.fonts.secondary};
  padding: 12px 0 0 30px;
`;

const ContractButton = styled.div`
  color: ${props => props.theme.colors.primary1};
  font-family: ${props => props.theme.fonts.secondary};
  margin: 20px 0 0;
  text-align: center;
  display: ${props => props.isVisible ? "block" : "none"};
  position: absolute;
  width: calc(100% - 40px);
  bottom: 20px;
`;

const PostalCode = styled.div`
  color: ${props => props.theme.colors.primary4};
  font-family: ${props => props.theme.fonts.secondary};
  font-size: ${props => props.theme.fontSizes.xs};
  padding-left: 30px;
`;

const DISTANCE_OPTIONS = [
  { value: 10, label: "10 Miles" },
  { value: 50, label: "50 Miles" },
  { value: 100, label: "100 Miles" },
  { value: 250, label: "250 Miles" },
];

const COUNTRY_CODES = [
  { value: "ca", label: "Canada" },
  { value: "gb", label: "United Kingdom" }, 
  { value: "pl", label: "Poland" },
  { value: "de", label: "Germany" }, 
  { value: "hu", label: "Hungary" }, 
  { value: "br", label: "Brazil" }, 
  { value: "jp", label: "Japan" }, 
  { value: "it", label: "Italy" }, 
  { value: "fr", label: "France" }, 
  { value: "cz", label: "Czech Republic" }, 
  { value: "at", label: "Austria" }, 
  { value: "au", label: "Australia" }, 
  { value: "mx", label: "Mexico" }, 
  { value: "nz", label: "New Zealand" }, 
  { value: "pr", label: "Puerto Rico" }
];

// These countires will be excluded from locally served postal code files due to huge data sets
const EXCLUDED_COUNTRIES = ["gb", "mx"];

function SearchForm(props) {
  const { apiBaseUrl } = useContext(EnvContext);
  const isMobile = mobileDetector();
  const { simplified, page404 } = props;
  const { ref, inView } = useInView({
    threshold: .1,
  });
  // If on the search results page the initial country code should derive from search values as props are undefined by the url
  const initialCountryCode = !!props.countryCode ? props.countryCode : props.searchValues && props.searchValues.countryCodeOption ? props.searchValues.countryCodeOption.value.toString() : "us";
  const initialCountryCodeOption = COUNTRY_CODES.find(element => element.value === initialCountryCode);
  const [zipcode, setZipcode] = useState("");
  const [countryCode, setCountryCode] = useState(initialCountryCode);
  const [conditions, setConditions] = useState([]);
  const [conditionId, setConditionId] = useState("");
  const [distance, setDistance] = useState("");
  const [zipError, setZipError] = useState(false);
  const [zipFocus, setZipFocus] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState("");
  const [selectedZipcode, setSelectedZipcode] = useState("");
  const [selectedDistance, setSelectedDistance] = useState(DISTANCE_OPTIONS[1]);
  const [selectedCountryCode, setSelectedCountryCode] = useState(initialCountryCodeOption);
  const [menuExpand, setMenuExpand] = useState(false);
  const [inputUpdated, setInputUpdated] = useState(false);

  useEffect(() => {
    if (!inputUpdated) {
      fetch(`${apiBaseUrl}/indications?filter=%7B%22order%22%3A%22name%22%2C%22where%22%3A%7B%22isArchived%22%3Afalse%7D%7D`).then(
        res => res.json()
      ).then(data => {
        setConditions(data);
      });
    
      if (props.searchValues) {
        if (props.searchValues.zipcodeOption && !selectedZipcode) {
          setSelectedZipcode(props.searchValues.zipcodeOption);
          setZipcode(props.searchValues.zipcodeOption.value.toString())
        }

        if (props.searchValues.distanceOption && !selectedDistance) {
          setSelectedDistance(props.searchValues.distanceOption);
          setDistance(props.searchValues.distanceOption.value.toString())
        }
        if (props.searchValues.conditionOption && !selectedCondition) {
          setSelectedCondition(props.searchValues.conditionOption);
          setConditionId(props.searchValues.conditionOption.value.toString())
        }

        if (props.searchValues.countryCodeOption && !selectedCountryCode) {
          setSelectedCountryCode(props.searchValues.countryCodeOption);
          setCountryCode(props.searchValues.countryCodeOption.value.toString())
        }
      }
    }
  }, [props.searchValues, apiBaseUrl, countryCode, selectedCondition, selectedCountryCode, selectedDistance, selectedZipcode, inputUpdated]);
  
  const buttonText = simplified ? "Search" : "Find a Study";

  const CustomValueContainer = ({ children, ...props }) => {
    return (
      <ValueContainer {...props}>
        <Placeholder {...props} isFocused={props.selectProps.menuIsOpen}>
          {props.selectProps.placeholder}
        </Placeholder>
        {React.Children.map(children, child =>
          child && child.type !== Placeholder ? child : null
        )}
      </ValueContainer>
    );
  };

  const CustomClearText = () => 'X';
  const ClearIndicator = props => {
    const {
      children = <CustomClearText />,
      getStyles,
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <div
        {...restInnerProps}
        ref={ref}
        style={getStyles('clearIndicator', props)}
      >
        <div style={
          {  
            cursor: 'pointer',
            borderRadius: '20px',
            border: '2px solid',
            padding: '3px 5px',
            fontWeight: 'bold',
            fontSize: '12px',
            color: '#828282',
            borderColor: '#828282',
            fontFamily: '"Proxima Nova", Helvetica, sans-serif'
          }
        }>{children}</div>
      </div>
    );
  };

  // React select custom styles
  const customStyles = {
    dropdownIndicator: provided => ({
      ...provided,
      display: 'none'
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: '0 0 6px 6px',
      color: props.theme.colors.neutral1,
      margin: 0,
      padding: 0,
      backgroundColor: props.theme.colors.neutral2,
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      height: '54px'
    }),
    menuList: provided => ({
      ...provided,
      fontFamily: props.theme.fonts.secondary,
      paddingTop: 0,
      paddingBottom: 0,
      maxHeight: '280px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? props.theme.colors.neutral2 : props.theme.colors.contrast2,
      color: props.theme.colors.neutral1,
      padding: 20,
      "&:hover": {
        backgroundColor: props.theme.colors.neutral2,
      },
    }),
    input: (provided, state)  => ({
      ...provided,
      fontFamily: props.theme.fonts.secondary,
      fontSize: '16px',
      height: '18px',
      marginTop: '2px'
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: props.theme.colors.neutral2,
      border: state.isFocused ? 'none' : 'none',
      borderBottom: state.isFocused ? `3px solid ${props.theme.colors.primary1}` : state.hasValue ? `3px solid ${props.theme.colors.primary4}` : `3px solid ${props.theme.colors.primary4}`,
      borderRadius: '6px 6px 0 0',
      boxShadow: 'none',
      width: '100%',
      height: '54px',
      "&:hover": {
        borderColor: props.theme.colors.primary1
      },
    }),
    indicatorSeparator: () => {},
    IndicatorsContainer: (provided, state) => ({
      ...provided,
      borderRadius: '20px',
      border: '3px solid',
      padding: '0',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      top: '26px',
      marginTop: '4px',
      "&:hover": {
        backgroundColor: props.theme.colors.primary1
      },
    }),
    container: provided => ({
      ...provided,
      width: '100%',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isFocused || state.hasValue ? props.theme.colors.primary1 : props.theme.colors.neutral3,
      position: 'absolute',
      top: state.isFocused || state.hasValue ? '6px' : '26px',
      fontSize: state.hasValue || state.isFocused ? '11px' : '16px',
      "&:hover": {
        
      },
    }),
  }

  function buildUrl(urlValues) {
    let apiUrl = `${apiBaseUrl}/studies/getNearbyStudies?`;
    urlValues.forEach(value => {
      const { property, label } = value;
      if (!!property) {
        apiUrl += `&${label}=${property}`;
      }
    })
    apiUrl += `&limit=20`;
    return apiUrl;
  }

  function getUrlValues() {
    const stateDistance = props.location && props.location.state && props.location.state.urlValues.find(element => element.label === "distance").property;
    return [
      { property: !!countryCode ? countryCode : 'us', label: 'countryCode' },
      { property: zipcode, label: 'postalCode' },
      { property: conditionId, label: 'indicationId' },
      { property: simplified ? distance : stateDistance ? stateDistance : "50", label: 'distance' }  
    ]
  }

  function handleSubmit(e) {
    const urlValues = getUrlValues();
    setInputUpdated(false);
    handleDistanceChange(DISTANCE_OPTIONS[1], fetchData(buildUrl(urlValues)));
    
    if (menuExpand) setMenuExpand(false);
    e.preventDefault();
  }

  function createSelectOptions(conditions) {
    let selectOptions = []
    conditions.forEach(condition => {
      selectOptions.push({ value: condition.id, label: condition.name })
    });
    return selectOptions;
  }

  function handleConditionChange(e) {
    if (e === null) {
      setConditionId("");
      setSelectedCondition(null)
    } else {
      setConditionId(e.value.toString());
      setSelectedCondition(e);
    }
    setInputUpdated(true);
  }

  function handleInputFocus(e) {
    setZipFocus(true);
  }

  function handleInputBlur(e) {
    setZipFocus(false);
  }

  function handleDistanceChange(e) {
    if (e === null) {
      setDistance("");
      setSelectedDistance(null);
    } else {
      setDistance(e.value.toString());
      setSelectedDistance(e);
    }
    setInputUpdated(true);
  }

  function handleCountryChange(e) {
    if (e === null) {
      setCountryCode("us");
      setSelectedCountryCode({ value: "us", label: "United States" });
    } else {
      setCountryCode(e.value.toString());
      setSelectedCountryCode(e);
    }
    // Clear postal code if the country is changed
    handleZipcodeChange(null)
    setInputUpdated(true);
  }

  function handleZipcodeChange(e) {
    if (e === null) {
      setZipcode("");
      setSelectedZipcode(null);
    } else {
      setZipcode(e.value.toString());
      setSelectedZipcode(e);
    }
    setInputUpdated(true);
  }

  function handleInputClear() {
    setZipcode("");
  }

  function fetchData(url) {
    fetch(url).then(
      res => res.json()
    ).then(data => {
      if (data.error) {
        setZipError(true)
        return;
      } else {
        setZipError(false)
      }
      const urlValues = getUrlValues();

      // filter by location if there is a distance and postal code except for studies coming soon
      function filterData(data, urlValues) {
        const postalCode = urlValues.find(element => element.label === "postalCode").property;
        const distanceValue = urlValues.find(element => element.label === "distance").property;
        if (!distanceValue || !postalCode) {
          return data;
        }
        const locationFilter = data.filter(study => {
          return study.distance <= Number.parseInt(distance) || !!study.landingcoming;
        })
        return locationFilter;
      }

      const searchData = { 
        studies: filterData(data.data, urlValues),
        searchValues: {
          zipcodeOption: selectedZipcode,
          conditionOption: selectedCondition,
          distanceOption: selectedDistance,
          countryCodeOption: selectedCountryCode
        },
        urlValues
      }

      props.router.navigate('/search', { state: searchData });
    });
  }

  return (
    <FormWrapper removeTopPadding={page404} simplified={simplified} ref={ref}>
      <MobileMenu hideMenu={(!isMobile || menuExpand) || !simplified}>
        <MobileHeader>{(selectedCondition && selectedCondition.label && selectedCondition.label.toString()) || "Select a condition"}</MobileHeader>
        <PostalCode>{zipcode || "Select a location"}</PostalCode>
        <ExpandButton onClick={() => setMenuExpand(true)}>open search</ExpandButton>
      </MobileMenu>
      <AnimationWrapper hide={page404} simplified={simplified}>
        <AnimationTop inView={inView} />
      </AnimationWrapper>
      <Title simplified={simplified}>I am looking for a clinical trial for</Title>
      <StyledForm id="search-form" hideMenu={isMobile && !menuExpand && simplified} isMobile={isMobile} simplified={simplified} onSubmit={handleSubmit}>  
        <Select 
          components={{ValueContainer: CustomValueContainer, ClearIndicator}}
          styles={customStyles} 
          defaultValue={props.searchValues ? props.searchValues.conditionOption : null}
          placeholder="Condition" 
          onChange={e => handleConditionChange(e)} 
          options={createSelectOptions(conditions)} 
          isClearable={true}
          id="search-select-condition"
          filterOption={createFilter({ matchFrom: 'any'})}
        />
        <InputContainer isMobile={isMobile} countryCodePresent={countryCode !== "us"} simplified={simplified}>
          <InnerTitle paddingValue="12px" simplified={simplified}>near</InnerTitle>
          <InputWrapper 
            hasValue={!!zipcode} 
            zipFocus={zipFocus}
            simplified={simplified}
            countryCodePresent={countryCode !== "us"}
            isMobile={isMobile}
          >
            {EXCLUDED_COUNTRIES.includes(countryCode) ?
              <>
                <StyledInput 
                  simplified={simplified}
                  zipFocus={zipFocus}
                  countryCodePresent={countryCode !== "us"}
                  type="text" 
                  value={zipcode} 
                  hasValue={!!zipcode}
                  zipError={zipError}
                  onChange={e => setZipcode(e.target.value)} 
                  onFocus={e => handleInputFocus(e)}
                  onBlur={e => handleInputBlur(e)}
                  id="search-input-postalcode"
                />
                <FakePlaceholder htmlFor="search-input-postalcode" hasValue={!!zipcode} zipFocus={zipFocus} simplified={simplified} />
                <ClearInput hasValue={!!zipcode} onClick={() => handleInputClear()}>X</ClearInput>
              </>
              :
              <AsyncInput
                countryCode={countryCode}
                onChange={e => handleZipcodeChange(e)}
                zipcodeOption={props.searchValues && props.searchValues.zipcodeOption}
                value={selectedZipcode}
              />
            }
            <ErrorMessage simplified={simplified} zipError={zipError}>We don’t recognize that postal code, please try again.</ErrorMessage>
          </InputWrapper>
          {(countryCode !== "us") && 
            <Select 
              components={{ValueContainer: CustomValueContainer, ClearIndicator}}
              styles={customStyles} 
              placeholder="Country" 
              onChange={e => handleCountryChange(e)} 
              options={COUNTRY_CODES} 
              isClearable={false}
              defaultValue={props.searchValues && props.searchValues.countryOption ? props.searchValues.countryOption : selectedCountryCode}
            />
          }
        </InputContainer>
        <SelectToggle countryCodePresent={countryCode !== "us"} isMobile={isMobile} simplified={simplified}>
          <Select 
            components={{ValueContainer: CustomValueContainer, ClearIndicator}}
            styles={customStyles} 
            placeholder="Distance" 
            onChange={e => handleDistanceChange(e)} 
            options={DISTANCE_OPTIONS} 
            isClearable={true}
            id="search-select-distance"
            defaultValue={props.searchValues && props.searchValues.distanceOption ? props.searchValues.distanceOption : DISTANCE_OPTIONS[1]}
          />
        </SelectToggle>
        <StyledButton simplified={simplified} id="search-form-submit" type="submit" value={buttonText} />
        <ContractButton isVisible={menuExpand && isMobile && simplified} onClick={() => setMenuExpand(false)}>close</ContractButton>
      </StyledForm>
      <SubHeader hide={page404} simplified={simplified}>
        <p><span>Participants</span> are the most</p>
        <p>important part of any study</p>
      </SubHeader>
      <AnimationWrapperBottom simplified={simplified}>
        <AnimationBottom inView={inView} />
      </AnimationWrapperBottom>
    </FormWrapper>
  )
}

export default withRouter(withTheme(SearchForm));